import type { GeneratorSizingWarning } from "../services/generatorSizing";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

type WarningCode = GeneratorSizingWarning["code"];

type WarningGroup = "not-established" | "not-evaluated" | "other";

interface GeneratorWarningPanelProps {
  warnings: GeneratorSizingWarning[];
}

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive maps: a new code without a label or a group is a compile error. A
// generator warning carries only a code and a message - there is no severity and
// no reference field on this contract, so none is shown.
const WARNING_CODE_LABELS: Record<WarningCode, string> = {
  DERATING_APPLIED: "Derating applied",
  TRANSIENT_REQUIREMENT_GOVERNS: "Transient requirement governs",
  TRANSIENT_PERFORMANCE_NOT_EVALUATED: "Transient performance not evaluated",
  NO_STANDARD_RATING_AVAILABLE: "No standard rating available",
  GROWTH_FACTOR_NOT_ESTABLISHED: "Future growth factor not established",
  DESIGN_MARGIN_NOT_ESTABLISHED: "Design margin factor not established",
  AMBIENT_DERATING_NOT_ESTABLISHED: "Ambient derating factor not established",
  ALTITUDE_DERATING_NOT_ESTABLISHED: "Altitude derating factor not established",
  STEP_LOAD_NOT_ESTABLISHED: "Step load not established",
  TRANSIENT_ALLOWANCE_NOT_ESTABLISHED: "Transient allowance factor not established",
};

// Where each code is listed. An input the engineer still owes comes first (it is
// why the result needs review), then what the method does not evaluate (A17,
// GAP-017), then everything else.
const WARNING_CODE_GROUPS: Record<WarningCode, WarningGroup> = {
  GROWTH_FACTOR_NOT_ESTABLISHED: "not-established",
  DESIGN_MARGIN_NOT_ESTABLISHED: "not-established",
  AMBIENT_DERATING_NOT_ESTABLISHED: "not-established",
  ALTITUDE_DERATING_NOT_ESTABLISHED: "not-established",
  STEP_LOAD_NOT_ESTABLISHED: "not-established",
  TRANSIENT_ALLOWANCE_NOT_ESTABLISHED: "not-established",
  TRANSIENT_PERFORMANCE_NOT_EVALUATED: "not-evaluated",
  DERATING_APPLIED: "other",
  TRANSIENT_REQUIREMENT_GOVERNS: "other",
  NO_STANDARD_RATING_AVAILABLE: "other",
};

const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-established", title: "Not established (review required)" },
  { group: "not-evaluated", title: "Not evaluated" },
  { group: "other", title: "Other warnings" },
];

export function GeneratorWarningPanel({ warnings }: GeneratorWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            // Within a group the engine's own order is kept.
            const members = warnings.filter(
              (warning) => WARNING_CODE_GROUPS[warning.code] === group,
            );
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning, index) => (
                    <li key={`${warning.code}-${index}`} data-warning-code={warning.code}>
                      <strong>{WARNING_CODE_LABELS[warning.code]}</strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export { WARNING_CODE_GROUPS, WARNING_CODE_LABELS };

export default GeneratorWarningPanel;
